// QRCustomizeStep.jsx - Updated to use Context
import React from 'react';
import styled from '@emotion/styled';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useQRContext } from '../context/QRContext';
import QRTemplateGrid from './QRTemplateGrid';
import QRColorPicker from './QRColorPicker';
import QRLogoUpload from './QRLogoUpload';
import QRAdvancedOptions from './QRAdvancedOptions';

const StepContent = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 2rem;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 1.75rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const StepHeader = styled.div`
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const StepTitle = styled.h2`
  color: white;
  font-size: clamp(1.5rem, 4vw, 1.75rem);
  font-weight: 700;
  margin: 0 0 0.5rem;
  line-height: 1.2;
`;

const StepDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: clamp(0.9rem, 1.1vw, 1rem);
  line-height: 1.5;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FormSection = styled.div`
  flex: 1;
  width: 100%;
  margin-bottom: 1.5rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const CompactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 480px) {
    gap: 1.25rem;
    margin-bottom: 1.25rem;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.25rem;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.875rem;
    border-radius: 12px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
  }
`;

const SectionTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.4;
`;

const SectionIcon = styled.div`
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 6px;
  background: linear-gradient(45deg, #7c3aed, #ec4899);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  flex-shrink: 0;
`;

const TemplateSection = styled.div`
  grid-column: 1 / -1;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.25rem;
  margin: 0 0 1.5rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.75rem;
    border-radius: 12px;
  }
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const Button = styled.button`
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(45deg, #7c3aed, #ec4899);
  color: white;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(124, 58, 237, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }
`;

const QRCustomizeStep = () => {
  const {
    qrOptions,
    setTemplate,
    setForegroundColor,
    setBackgroundColor,
    setLogoFile,
    setLogoSize,
    updateQROptions,
    nextStep,
    previousStep,
  } = useQRContext();

  const handleTemplateSelect = (template) => {
    setTemplate(template);
  };

  const handleAdvancedOptionsChange = (updates) => {
    updateQROptions(updates);
  };

  return (
    <StepContent>
      <StepHeader>
        <StepTitle>Customize Your Design</StepTitle>
        <StepDescription>
          Personalize your QR code with templates, colors, and advanced options.
        </StepDescription>
      </StepHeader>

      <FormSection>
        <TemplateSection>
          <SectionTitle>
            <SectionIcon>🎨</SectionIcon>
            Choose Style
          </SectionTitle>
          <QRTemplateGrid
            selectedTemplate={qrOptions.template}
            onTemplateSelect={handleTemplateSelect}
          />
        </TemplateSection>

        <CompactGrid>
          <Section>
            <SectionTitle>
              <SectionIcon>🎨</SectionIcon>
              Colors
            </SectionTitle>
            <QRColorPicker
              foregroundColor={qrOptions.foregroundColor}
              backgroundColor={qrOptions.backgroundColor}
              onForegroundChange={setForegroundColor}
              onBackgroundChange={setBackgroundColor}
            />
          </Section>

          <Section>
            <SectionTitle>
              <SectionIcon>🖼️</SectionIcon>
              Logo
            </SectionTitle>
            <QRLogoUpload
              logoFile={qrOptions.logoFile}
              logoSize={qrOptions.logoSize}
              onLogoChange={setLogoFile}
              onSizeChange={setLogoSize}
            />
          </Section>
        </CompactGrid>

        <QRAdvancedOptions
          options={qrOptions}
          onChange={handleAdvancedOptionsChange}
        />
      </FormSection>

      <NavigationButtons>
        <SecondaryButton onClick={previousStep}>
          <ArrowLeft size={16} />
          Back
        </SecondaryButton>
        <PrimaryButton onClick={nextStep}>
          Continue
          <ArrowRight size={16} />
        </PrimaryButton>
      </NavigationButtons>
    </StepContent>
  );
};

export default QRCustomizeStep;
